/**
 * Format bytes to human-readable string
 * @param bytes - Number of bytes to format
 * @returns Formatted string (e.g., "1.5 GB")
 */
export const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Format duration in seconds to human-readable string
 * @param seconds - Duration in seconds
 * @param includeSeconds - Whether to include seconds in output (default: true)
 * @returns Formatted string (e.g., "2h 15m 30s")
 */
export const formatDuration = (seconds: number, includeSeconds: boolean = true): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return includeSeconds
      ? `${hours}h ${minutes}m ${secs}s`
      : `${hours}h ${minutes}m`;
  } else if (minutes > 0) {
    return includeSeconds
      ? `${minutes}m ${secs}s`
      : `${minutes}m`;
  } else {
    return `${secs}s`;
  }
};

/**
 * Format ISO date string to localized date/time
 * @param dateString - ISO 8601 date string or undefined
 * @param options - Intl.DateTimeFormatOptions (optional)
 * @returns Formatted date string or 'Never' if undefined
 */
export const formatDate = (
  dateString?: string,
  options?: Intl.DateTimeFormatOptions
): string => {
  if (!dateString) return 'Never';

  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  };

  return new Date(dateString).toLocaleDateString('en-US', options || defaultOptions);
};

/**
 * Format relative time (e.g., "2 hours ago", "3 days ago")
 * @param dateString - ISO 8601 date string
 * @returns Relative time string
 */
export const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 7) {
    return formatDate(dateString);
  } else if (diffDays > 0) {
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  } else if (diffHours > 0) {
    return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  } else if (diffMins > 0) {
    return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  } else {
    return 'Just now';
  }
};
